import type { BaseItemSchema, SessionResponse } from "@memorang/types";
import type { Hint, Mode } from "@memorang/types";
import { create } from "zustand";
import { SESSION_EVENTS } from "../constants";
import type {
	Answer,
	AttemptState,
	BlockState,
	ItemState,
	SessionEvent,
	SessionState,
	UpdateItemStatesPayload,
} from "./types";

const useSessionStore = create<SessionState>()((set) => ({
	sessionItems: [],
	currentItemIndex: 0,
	currentProgress: 0,
	id: 0,
	numericId: 0,
	isSummativeTest: false,
	isDiagnostic: false,
	collectionId: 0,
	currentBlockIndex: 0,
	numItemsRequested: 0,
	breakTimeElapsed: 0,
	mode: "QUESTION",
	totalTime: 0,
	attempts: new Map<string, AttemptState>(),
	sessionTime: 0,
	numGoal: 0,
	inReviewMode: false,
	timeElapsed: 0,
	confettiCannons: new Map<string, boolean>(),
	startTime: "",
	blockStates: new Map<number, BlockState>(),
	itemStates: new Map<number, ItemState>(),
	sessionEvents: new Map<number, SessionEvent[]>(),
	itemViewedTimestamps: new Map<number, string>(),
	updateNumericId: (numericId: number) => {
		set({ numericId });
	},
	updateShowConfetti: ({
		itemId,
		showConfetti,
	}: { itemId: string; showConfetti: boolean }) => {
		set((state) => {
			return {
				confettiCannons: new Map(state.confettiCannons).set(
					itemId,
					showConfetti,
				),
			};
		});
	},

	updateBreakTimeElapsed: (breakTimeElapsed: number) => {
		set({ breakTimeElapsed });
	},
	updateCompletedTasks: (itemId: number, completed: boolean) => {
		set((state) => {
			const completedTasks = state.completedTasks || {};
			completedTasks[itemId] = completed;
			return { completedTasks: { ...completedTasks } };
		});
	},
	updateItemAttempts: ({
		itemId,
		selectedChoiceId,
		correct,
		crossOut,
		answered,
		showTryAgain,
		attempted,
	}: {
		itemId: string;
		selectedChoiceId?: string;
		correct?: boolean;
		crossOut?: boolean;
		answered?: boolean;
		showTryAgain?: boolean;
		attempted?: boolean;
	}) => {
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
		set((state) => {
			const attempts = new Map(state.attempts);
			let attempt = attempts.get(itemId) || { choiceAttempts: {} };

			if (crossOut && selectedChoiceId) {
				const currentChoiceIdCrossedOut =
					attempt.choiceAttempts[selectedChoiceId]?.crossOut;
				if (currentChoiceIdCrossedOut) {
					delete attempt.choiceAttempts[selectedChoiceId];
				} else {
					attempt.choiceAttempts[selectedChoiceId] = {
						correct,
						crossOut: true,
					};
				}
			} else if (selectedChoiceId) {
				if (
					attempt.currentSelectedChoiceId === selectedChoiceId &&
					answered == null
				) {
					delete attempt.choiceAttempts[selectedChoiceId];
					attempt.currentSelectedChoiceId = undefined;
				} else {
					if (answered == null) {
						attempt.choiceAttempts = Object.fromEntries(
							Object.entries(attempt.choiceAttempts).filter(
								([_, value]) => value.attempted,
							),
						);
					}

					attempt.choiceAttempts[selectedChoiceId] = {
						...attempt.choiceAttempts[selectedChoiceId],
						correct,
						crossOut: false,
						attempted: attempted,
					};
					attempt.currentSelectedChoiceId = selectedChoiceId;
				}
			}

			attempt = {
				...attempt,
				answered: attempt.answered || answered,
				showTryAgain,
				currentSelectedChoiceId: attempt.showTryAgain
					? undefined
					: attempt.currentSelectedChoiceId,
			};

			attempts.set(itemId, attempt);
			return { attempts };
		});
	},
	updateTimeElapsed: (timeElapsed: number) => {
		set({ timeElapsed });
	},
	updateAnswerEventInProgress: (isAnswerEventInProgress: boolean) => {
		set({ isAnswerEventInProgress });
	},
	updateEndingSessionInProgress: (isEndingSessionInProgress: boolean) => {
		set({ isEndingSessionInProgress });
	},
	updateCurrentBlockIndex: (index: number) => {
		set({ currentBlockIndex: index });
	},

	toggleShowMoreItemsAlert: () => {
		set((state) => {
			return {
				showMoreItemsAlert: !state.showMoreItemsAlert,
			};
		});
	},
	toggleShowBreakDialog: (show?: boolean) => {
		set(() => {
			return {
				showBreakDialog: show,
			};
		});
	},
	toggleBreakOngoing: (isBreakOngoing: boolean) => {
		set(() => {
			return {
				isBreakOngoing,
			};
		});
	},
	updateBreak: (breakData?: {
		startTime: string;
		allotedTime: number;
		status: "STARTED" | "EXPIRED";
		totalBreakTimeTakenInSeconds?: number;
	}) => {
		if (breakData) {
			set((state) => {
				return {
					breaks: [...(state.breaks || []), breakData],
				};
			});
		} else {
			set(() => {
				return {
					breaks: [],
				};
			});
		}
	},
	updateSession: ({
		sessionResponse,
		isReverseMode,
		mode,
		isSummativeTest,
		inReviewMode,
		numItemsRequested,
		isRecommendedTask,
		isDiagnostic,
	}: {
		sessionResponse: SessionResponse;
		isReverseMode?: boolean;
		mode?: Mode;
		isSummativeTest?: boolean;
		inReviewMode?: boolean;
		numItemsRequested?: number;
		isRecommendedTask?: boolean;
		isDiagnostic?: boolean;
	}) => {
		const initBlockStates = isSummativeTest
			? sessionResponse.sessionItems.reduce((acc, _, index) => {
					acc.set(index, {
						currentProgress: 0,
						completed: false,
						completedStatuses: {},
						markedItemsForReview: [],
					});
					return acc;
				}, new Map<number, BlockState>())
			: new Map<number, BlockState>();

		const numGoal = sessionResponse.sessionItems[0].length;

		const showMoreItemsAlert =
			(numItemsRequested || 0) < numGoal &&
			!isSummativeTest &&
			!isDiagnostic &&
			!inReviewMode;

		const isOlx = sessionResponse.examName?.includes("OLX");

		const startTime = sessionResponse.startTime;
		const totalTime = sessionResponse.totalTime || 0;

		const examBreak = sessionResponse.examBreak?.[0];

		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
		const getOlxElapsedTime = () => {
			if (startTime) {
				const currentTime = Date.now();
				const totalElapsed = Math.floor(
					(currentTime - new Date(startTime).getTime()) / 1000,
				);
				let timeElapsed = totalElapsed;

				const firstBreak = examBreak;
				if (firstBreak?.status && firstBreak.status === "EXPIRED") {
					set({
						serverExamBreak: examBreak,
					});
					const totalBreakTimeTakenInSeconds =
						firstBreak.totalBreakTimeTakenInSeconds || 0;
					timeElapsed = totalElapsed - totalBreakTimeTakenInSeconds;
				}

				if (
					firstBreak?.status &&
					firstBreak.status !== "EXPIRED" &&
					firstBreak.startTime
				) {
					// break is ongoing
					const breakStartTime = firstBreak?.startTime;
					const breakAllotedTime = firstBreak?.allotedTime || 0;

					// Calculate actual break time taken
					const actualBreakTime = breakStartTime
						? Math.floor(
								(currentTime - new Date(breakStartTime).getTime()) / 1000,
							)
						: 0;

					// Use the lesser of actual break time or allotted break time
					const breakTimeToSubtract = Math.min(
						actualBreakTime,
						breakAllotedTime,
					);
					const breakTimeRemaining = breakAllotedTime - actualBreakTime;
					if (breakTimeRemaining > 0) {
						set({
							breakTimeElapsed: actualBreakTime,
							isBreakOngoing: true,
							showBreakDialog: true,
							breaks: [
								{
									startTime: breakStartTime,
									allotedTime: breakAllotedTime,
									status: "STARTED",
								},
							],
							serverExamBreak: examBreak,
						});
					}
					timeElapsed = Math.max(0, totalElapsed - breakTimeToSubtract);
				}

				if (firstBreak && !firstBreak.status) {
					set({
						serverExamBreak: firstBreak,
					});
				}

				return timeElapsed;
			}
			return 0;
		};
		const finalSessionTime = isOlx
			? getOlxElapsedTime()
			: sessionResponse.sessionTime || 0;
		set({
			sessionItems: sessionResponse.sessionItems,
			numGoal,
			id: sessionResponse.id,
			isReverseMode,
			examName: sessionResponse.examName,
			mode,
			numericId: sessionResponse.numericId,
			isSummativeTest,
			startTime,
			blockStates: initBlockStates,
			inReviewMode: inReviewMode,
			showMoreItemsAlert,
			sessionTime: finalSessionTime,
			collectionId: sessionResponse.collectionId,
			totalTime,
			numItemsRequested: numItemsRequested || 0,
			isRecommendedTask,
			isEndingSessionInProgress: true,
			isDiagnostic,
			sessionSubType: sessionResponse.sessionSubType,
			sessionType: sessionResponse.sessionType,
		});
	},
	updateAnswers: (id: number, answer: Answer) => {
		set((state) => {
			const answers = state.answers || new Map<number, Answer>();
			const selectedChoiceIds =
				state.itemStates?.get(id)?.selectedChoiceIds || [];
			answers.set(id, {
				...answer,
				selectedChoiceIds: selectedChoiceIds,
				answeredFormStates: answer.answeredFormStates,
			});
			return {
				answers: new Map(answers),
			};
		});
	},

	updateProgress: (progress: number) => {
		set((state) => {
			const { currentBlockIndex, isSummativeTest } = state;
			const existingBlockState = state.blockStates.get(currentBlockIndex);
			if (isSummativeTest) {
				const newBlockStates = new Map(state.blockStates);
				newBlockStates.set(currentBlockIndex, {
					...(existingBlockState || {}),
					currentProgress: progress,
				});
				return { blockStates: newBlockStates };
			}
			return { currentProgress: progress };
		});
	},
	updateProgressForEachBlock: (progress: number, blockId: number) => {
		set((state) => {
			const blockStates = state.blockStates || new Map<number, BlockState>();
			const blockState = blockStates.get(blockId) || {};
			blockStates.set(blockId, { ...blockState, currentProgress: progress });
			return { blockStates: new Map(blockStates) };
		});
	},
	updateHighlightedItems: (itemId: number, highlightedHtml?: string) =>
		set((state) => {
			const { currentBlockIndex } = state;
			const blockId = currentBlockIndex;
			const existingBlockState = state.blockStates.get(blockId!);
			const currentHighlightedItems =
				existingBlockState?.highlightedItems || new Map<number, string>();

			const updatedHighlightedItems = new Map(currentHighlightedItems);
			updatedHighlightedItems.set(itemId, highlightedHtml);

			const updatedBlockState = {
				...existingBlockState,
				highlightedItems: updatedHighlightedItems,
			};

			const updatedBlockStates = new Map(state.blockStates);
			updatedBlockStates.set(blockId!, updatedBlockState);

			return {
				blockStates: updatedBlockStates,
			};
		}),
	updateItemStates: ({
		itemId,
		selectedChoiceId,
		multiSelect,
		correctAnswerIds,
		crossOut,
		scores,
		answered,
	}: UpdateItemStatesPayload) => {
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
		set((state) => {
			const itemStates = state.itemStates || new Map<number, ItemState>();
			const itemState = itemStates.get(itemId);
			let selectedChoiceIds = itemState?.selectedChoiceIds || [];
			const crossedOutChoiceIds = itemState?.crossedOutChoiceIds || [];
			const hints = itemState?.hints;
			const index = selectedChoiceIds.indexOf(selectedChoiceId);
			const crossOutIndex = crossedOutChoiceIds.indexOf(selectedChoiceId);

			if (crossOut) {
				if (crossOutIndex > -1) {
					crossedOutChoiceIds.splice(crossOutIndex, 1);
				} else {
					crossedOutChoiceIds.push(selectedChoiceId);
					if (index > -1) {
						selectedChoiceIds.splice(index, 1);
					}
				}
			} else {
				if (multiSelect) {
					if (index > -1) {
						selectedChoiceIds.splice(index, 1);
					} else {
						selectedChoiceIds.push(selectedChoiceId);
					}
				} else if (!correctAnswerIds?.length) {
					if (index > -1) {
						selectedChoiceIds = [];
					} else {
						selectedChoiceIds = [selectedChoiceId];
					}
				}
				if (crossOutIndex > -1) {
					crossedOutChoiceIds.splice(crossOutIndex, 1);
				}
			}

			itemStates.set(itemId, {
				selectedChoiceIds: [...selectedChoiceIds],
				correctAnswerIds,
				crossedOutChoiceIds: [...crossedOutChoiceIds],
				scores: scores || [],
				answered: answered,
				hints,
			});
			return { itemStates: new Map(itemStates) };
		});
	},
	updateHintsForItem: (itemId: number, hints: Hint[]) =>
		set((state) => {
			const itemStates = state.itemStates || new Map();
			const itemState = itemStates.get(itemId);
			const newItemState = {
				...itemState,
				scores: itemState?.scores || [],
				hints,
			};
			itemStates.set(itemId, newItemState);
			return {
				itemStates: new Map(itemStates),
			};
		}),
	updateInReview: (inReviewMode: boolean, sessionItems: BaseItemSchema[][]) =>
		set((state) => {
			if (inReviewMode) {
				return {
					inReviewMode,
					persistedCurrentProgress: state.currentProgress,
					currentProgress: 0,
					currentItemIndex: 0,
					persistedItems: state.sessionItems,
					sessionItems,
					numGoal: sessionItems.length,
				};
			}
			return {
				inReviewMode,
				currentProgress: state.persistedCurrentProgress,
				items: state.persistedSessionItems,
			};
		}),
	updateMarkedItemsForReview: (itemId: number) =>
		set((state) => {
			const { currentBlockIndex } = state;
			const blockId = currentBlockIndex;
			const existingBlockState = state.blockStates.get(blockId!);
			const currentMarkedItems = existingBlockState?.markedItemsForReview || [];
			const isItemMarked = currentMarkedItems.includes(itemId);

			const updatedMarkedItems = isItemMarked
				? currentMarkedItems.filter((id) => id !== itemId)
				: [...currentMarkedItems, itemId];

			const updatedBlockState = {
				...existingBlockState,
				markedItemsForReview: updatedMarkedItems,
			};

			const updatedBlockStates = new Map(state.blockStates);
			updatedBlockStates.set(blockId!, updatedBlockState);

			return {
				blockStates: updatedBlockStates,
			};
		}),
	resetSessionStore: () =>
		set({
			currentProgress: 0,
			itemStates: undefined,
			answers: undefined,
			numGoal: 0,
			isSummativeTest: false,
			isReverseMode: false,
			inReviewMode: false,
			timeElapsed: 0,
			currentBlockIndex: 0,
			blockStates: new Map<number, BlockState>(),
			totalTime: 0,
			sessionTime: 0,
			numItemsRequested: 0,
			isEndingSessionInProgress: false,
			isAnswerEventInProgress: false,
			breaks: [],
			breakTimeElapsed: 0,
			isBreakOngoing: false,
			completedTasks: {},
			isDiagnostic: false,
			collectionId: 0,
			id: 0,
			attempts: new Map<string, AttemptState>(),
			confettiCannons: new Map<string, boolean>(),
			sessionEvents: new Map(),
			itemViewedTimestamps: new Map(),
		}),
	addSessionEvent: (itemId: number, event: Omit<SessionEvent, "ts">) =>
		set((state) => {
			const sessionEvents = new Map(state.sessionEvents);
			const events = sessionEvents.get(itemId) || [];
			const newEvent = {
				...event,
				ts: new Date().toISOString(),
			};
			sessionEvents.set(itemId, [...events, newEvent]);
			return { sessionEvents };
		}),

	clearSessionEvents: (itemId: number) =>
		set((state) => {
			const sessionEvents = new Map(state.sessionEvents);
			const itemViewedTimestamps = new Map(state.itemViewedTimestamps);
			sessionEvents.delete(itemId);
			itemViewedTimestamps.delete(itemId);
			return { sessionEvents, itemViewedTimestamps };
		}),
	trackItemViewed: (itemId: number) =>
		set((state) => {
			const itemViewedTimestamps = new Map(state.itemViewedTimestamps);

			if (!itemViewedTimestamps.has(itemId)) {
				const timestamp = new Date().toISOString();
				itemViewedTimestamps.set(itemId, timestamp);

				const sessionEvents = new Map(state.sessionEvents);
				const events = sessionEvents.get(itemId) || [];
				sessionEvents.set(itemId, [
					...events,
					{
						ts: timestamp,
						name: SESSION_EVENTS.ITEM_VIEWED,
					},
				]);

				return { sessionEvents, itemViewedTimestamps };
			}
			return {};
		}),
}));

export default useSessionStore;
